import React from 'react';
import { ShieldCheck, Zap, Users, Sparkles, CheckCircle2, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

export default function About() {
  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 font-sans pb-20">
      
      {/* Visual Banner Header */}
      <div className="relative overflow-hidden bg-slate-900 py-20 px-4 md:px-8 mb-12 shadow-xl">
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-30 transform scale-105"
          style={{ backgroundImage: `url('https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?auto=format&fit=crop&w=1600&q=80')` }}
        ></div>
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/60 to-transparent"></div>

        <div className="relative max-w-4xl mx-auto text-center z-10">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-white/10 backdrop-blur-md border border-white/20 text-white rounded-full text-xs font-semibold uppercase tracking-wider mb-5 shadow-sm">
            <Sparkles className="w-3.5 h-3.5 text-amber-400" /> About PrimiumFlats
          </div>
          <h1 className="text-3xl md:text-5xl font-extrabold mb-4 tracking-tight text-white drop-shadow-sm">
            Crafting Elite Real Estate Experiences
          </h1>
          <p className="text-slate-200 text-base md:text-lg max-w-2xl mx-auto font-light leading-relaxed">
            Your definitive gateway to discovering verified luxury properties, architectural masterpieces, and dream homes across India.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Main Intro & Stats Box */}
        <div className="bg-white border border-slate-200/80 rounded-3xl p-8 md:p-14 shadow-xl shadow-slate-200/50 mb-16 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-50 rounded-full blur-3xl pointer-events-none -mr-20 -mt-20"></div>
          
          <div className="max-w-3xl mx-auto text-center relative z-10">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4 tracking-tight">Redefining Property Discovery</h2>
            <p className="text-slate-600 text-base md:text-lg leading-relaxed font-normal">
              Welcome to <span className="font-semibold text-indigo-600">PrimiumFlats</span>, India's premier digital real estate ecosystem. We seamlessly connect discerning buyers, verified sellers, and tenants through high-speed cloud infrastructure, complete transparency, and absolute zero brokerage barriers.
            </p>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-12 pt-10 border-t border-slate-100 relative z-10">
            <div className="text-center p-5 rounded-2xl bg-slate-50 border border-slate-200/60">
              <h4 className="text-3xl font-extrabold text-indigo-600 mb-1 tracking-tight">10K+</h4>
              <p className="text-slate-500 text-xs font-semibold uppercase tracking-wider">Verified Listings</p>
            </div>
            <div className="text-center p-5 rounded-2xl bg-slate-50 border border-slate-200/60">
              <h4 className="text-3xl font-extrabold text-indigo-600 mb-1 tracking-tight">15+</h4>
              <p className="text-slate-500 text-xs font-semibold uppercase tracking-wider">Prime Cities</p>
            </div>
            <div className="text-center p-5 rounded-2xl bg-slate-50 border border-slate-200/60">
              <h4 className="text-3xl font-extrabold text-indigo-600 mb-1 tracking-tight">99%</h4>
              <p className="text-slate-500 text-xs font-semibold uppercase tracking-wider">Satisfied Clients</p>
            </div>
            <div className="text-center p-5 rounded-2xl bg-slate-50 border border-slate-200/60">
              <h4 className="text-3xl font-extrabold text-indigo-600 mb-1 tracking-tight">₹0</h4>
              <p className="text-slate-500 text-xs font-semibold uppercase tracking-wider">Brokerage Charges</p>
            </div>
          </div>
        </div>

        {/* Core Features Section */}
        <div className="mb-16">
          <div className="text-center mb-12">
            <span className="text-indigo-600 text-xs font-bold uppercase tracking-widest block mb-2">Why Choose Us</span>
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight">Engineered for Trust & Speed</h2>
            <p className="text-slate-500 text-sm md:text-base mt-2 max-w-xl mx-auto font-normal">We eliminate the traditional complexities of real estate with next-gen technical solutions.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white border border-slate-200/80 rounded-3xl p-8 shadow-xl shadow-slate-200/50 hover:border-indigo-200 hover:shadow-2xl transition-all duration-300 flex flex-col justify-between">
              <div>
                <div className="w-14 h-14 bg-indigo-50 text-indigo-600 rounded-2xl flex items-center justify-center mb-6 shadow-sm">
                  <ShieldCheck className="w-7 h-7" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">100% Verified Listings</h3>
                <p className="text-slate-600 text-sm leading-relaxed font-normal">
                  Every single property is rigorously vetted and published by authenticated users to guarantee authentic deals and secure transactions.
                </p>
              </div>
              <ul className="mt-6 pt-4 border-t border-slate-100 space-y-2.5 text-xs font-medium text-slate-600">
                <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600" /> Zero fake postings</li>
                <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600" /> Direct owner approval</li>
              </ul>
            </div>

            <div className="bg-white border border-slate-200/80 rounded-3xl p-8 shadow-xl shadow-slate-200/50 hover:border-indigo-200 hover:shadow-2xl transition-all duration-300 flex flex-col justify-between">
              <div>
                <div className="w-14 h-14 bg-indigo-50 text-indigo-600 rounded-2xl flex items-center justify-center mb-6 shadow-sm">
                  <Zap className="w-7 h-7" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">Live Cloud Architecture</h3>
                <p className="text-slate-600 text-sm leading-relaxed font-normal">
                  Powered by advanced real-time cloud databases ensuring lightning-fast search queries, instant updates, and live postings.
                </p>
              </div>
              <ul className="mt-6 pt-4 border-t border-slate-100 space-y-2.5 text-xs font-medium text-slate-600">
                <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600" /> Real-time database sync</li>
                <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600" /> Instant live preview</li>
              </ul>
            </div>

            <div className="bg-white border border-slate-200/80 rounded-3xl p-8 shadow-xl shadow-slate-200/50 hover:border-indigo-200 hover:shadow-2xl transition-all duration-300 flex flex-col justify-between">
              <div>
                <div className="w-14 h-14 bg-indigo-50 text-indigo-600 rounded-2xl flex items-center justify-center mb-6 shadow-sm">
                  <Users className="w-7 h-7" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">Direct Owner Connect</h3>
                <p className="text-slate-600 text-sm leading-relaxed font-normal">
                  Interact directly with verified property builders and owners without middlemen, saving thousands in heavy brokerage commissions.
                </p>
              </div>
              <ul className="mt-6 pt-4 border-t border-slate-100 space-y-2.5 text-xs font-medium text-slate-600">
                <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600" /> Zero middlemen fees</li>
                <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600" /> Direct chat support</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Call to Action Banner */}
        <div className="bg-slate-900 rounded-3xl p-8 md:p-14 text-white shadow-2xl relative overflow-hidden flex flex-col md:flex-row items-center justify-between gap-8 border border-slate-800">
          <div className="absolute -left-20 -bottom-20 w-72 h-72 bg-indigo-600/20 rounded-full blur-3xl pointer-events-none"></div>
          
          <div className="relative z-10">
            <span className="text-indigo-400 font-semibold text-xs uppercase tracking-widest block mb-2">Get Started Today</span>
            <h3 className="text-2xl md:text-3xl font-extrabold mb-3 tracking-tight">Ready to find your dream property?</h3>
            <p className="text-slate-300 text-sm md:text-base max-w-xl font-light">
              Explore thousands of handpicked flats, architectural villas, and luxury penthouses across elite locations right now.
            </p>
          </div>
          
          <Link 
            to="/properties" 
            className="relative z-10 inline-flex items-center gap-2 px-8 py-4 bg-indigo-600 hover:bg-indigo-500 text-white font-semibold rounded-2xl shadow-lg shadow-indigo-600/30 transition-all duration-300 hover:scale-105 whitespace-nowrap text-xs uppercase tracking-wider"
          >
            Browse All Properties <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

      </div>
    </div>
  );
}